import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import { flag } from '../utils/countryFlags';
import { normalize } from '../utils/textUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Label } from '../components/ui/label';
import { 
  Users, 
  Package, 
  DollarSign, 
  CheckCircle, 
  XCircle,
  Eye,
  TrendingUp,
  Percent,
  Clock,
  ShieldCheck,
  Shield,
  AlertCircle,
  AlertTriangle,
  MessageSquare,
  Send,
  Headphones,
  Archive,
  Plane,
  Truck,
  MapPin,
  Timer,
  CreditCard,
  Banknote,
  Smartphone,
  Building,
  Luggage,
  ArrowLeft,
  LayoutGrid,
  FileText,
  History
} from 'lucide-react';
import { Star, Mail, Filter, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserScoreManager from '../components/UserScoreManager';
import AdminListingsManager from '../components/AdminListingsManager';
import OrderDetailModal from '../components/OrderDetailModal';
import { ContactUserModal } from '../components/ContactUserModal';
import AdminCurrencySection from '../components/AdminCurrencySection';

// Statuts de commande pour la logistique centralisée
const ORDER_STATUSES = {
  pending_admin_validation: 'En attente de validation',
  pending_payment: 'En attente de paiement',
  paid: 'Paiement confirmé',
  awaiting_pickup_seller: 'En attente récupération vendeur',
  received_admin_departure: 'Reçu Admin - Départ',
  in_transit: 'En transit',
  received_admin_destination: 'Reçu Admin - Destination',
  delivered: 'Livré',
  completed: 'Complété',
  rejected: 'Refusée',
  cancelled: 'Annulée',
  refunded: 'Remboursée'
};

const STATUS_COLORS = {
  pending_admin_validation: 'bg-amber-500/20 text-amber-600',
  pending_payment: 'bg-yellow-500/20 text-yellow-500',
  paid: 'bg-amber-500/20 text-amber-500',
  awaiting_pickup_seller: 'bg-orange-500/20 text-orange-500',
  received_admin_departure: 'bg-teal-500/20 text-teal-500',
  in_transit: 'bg-violet-500/20 text-violet-500',
  received_admin_destination: 'bg-sky-500/20 text-sky-500',
  delivered: 'bg-green-500/20 text-green-500',
  completed: 'bg-emerald-500/20 text-emerald-500',
  cancelled: 'bg-red-500/20 text-red-500',
  rejected: 'bg-red-500/20 text-red-500',
  refunded: 'bg-gray-500/20 text-gray-500'
};

const PAYMENT_METHOD_LABELS = {
  card: 'Carte',
  interac: 'Interac',
  orange_money: 'Orange Money',
  cash: 'Espèces'
};

const PAYMENT_METHOD_ICONS = {
  card: CreditCard,
  interac: Building,
  orange_money: Smartphone,
  cash: Banknote
};

const TYPE_LABELS_SHORT = {
  kilos: 'kg', sacs: 'sac', enveloppes: 'env.', telephones: 'tél.',
  ordinateurs: 'ordi.', sac_23kg: 'sac 23kg'
};
function formatOrderBaggage(order) {
  if (order.selected_items && order.selected_items.length > 0) {
    return order.selected_items.map(function(it) {
      return it.quantity + ' ' + (TYPE_LABELS_SHORT[it.type] || it.type);
    }).join(', ');
  }
  if (order.baggage_type) {
    return order.quantity + ' ' + (TYPE_LABELS_SHORT[order.baggage_type] || order.baggage_type);
  }
  return order.quantity + ' unité(s)';
}


const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingListings, setPendingListings] = useState([]);
  const [canceledListings, setCanceledListings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [awaitingConfirmOrders, setAwaitingConfirmOrders] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminSection, setAdminSection] = useState('overview'); // 'overview', 'baggage', 'currency', 'users'
  const [currencyNavTab, setCurrencyNavTab] = useState(null);
  const [currencyNavOrderBuyer, setCurrencyNavOrderBuyer] = useState(undefined);
  const [currencyNavListingSeller, setCurrencyNavListingSeller] = useState(undefined);
  const [commissionRate, setCommissionRate] = useState('');
  const [sellerCommissionRate, setSellerCommissionRate] = useState('');
  const [disputeFilter, setDisputeFilter] = useState('all');
  const [ticketFilter, setTicketFilter] = useState('all');
  const [viewingTicket, setViewingTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [confirmPaymentDialog, setConfirmPaymentDialog] = useState(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [viewingOrderDetail, setViewingOrderDetail] = useState(null);
  const [viewingUserDetail, setViewingUserDetail] = useState(null);
  
  // Insurance settings state
  const [insuranceBaseCoverage, setInsuranceBaseCoverage] = useState('');
  const [insuranceRate, setInsuranceRate] = useState('');
  const [insuranceClaimDelay, setInsuranceClaimDelay] = useState('');
  
  // Platform reviews state
  const [platformReviews, setPlatformReviews] = useState([]);

  // Currency platform state
  const [currencyStats, setCurrencyStats] = useState(null);
  const [currencyPendingListings, setCurrencyPendingListings] = useState([]);
  const [currencyAllListings, setCurrencyAllListings] = useState([]);
  const [currencyOrders, setCurrencyOrders] = useState([]);
  const [currencySettings, setCurrencySettings] = useState({ min_xaf: 400, max_xaf: 450, commission_rate: 5 });
  
  // All orders state
  const [allOrders, setAllOrders] = useState([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderPaymentFilter, setOrderPaymentFilter] = useState('all');
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [orderBuyerFilter, setOrderBuyerFilter] = useState('');
  const [orderSellerFilter, setOrderSellerFilter] = useState('');
  const [orderDepartFilter, setOrderDepartFilter] = useState('');
  const [orderDestFilter, setOrderDestFilter] = useState('');
  
  // Contact modal state
  const [contactModal, setContactModal] = useState({ open: false, users: [], contextType: '', contextId: '' });

  const [userSearch, setUserSearch] = useState('');
  const [userSortBy, setUserSortBy] = useState('date_desc');
  const [activeTab, setActiveTab] = useState('pending-validation');
  const [listingSellerFilter, setListingSellerFilter] = useState('');
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null); // {id, name}

  // Orders pending validation
  const [pendingValidationOrders, setPendingValidationOrders] = useState([]);
  const [rejectModal, setRejectModal] = useState({ open: false, orderId: '', reason: '' });
  const [historyModal, setHistoryModal] = useState({ open: false, listingId: '', data: [], loading: false });

  const fetchListingHistory = async (listingId) => {
    setHistoryModal({ open: true, listingId, data: [], loading: true });
    try {
      const res = await axios.get(`${API}/currency/admin/listings/${listingId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryModal(prev => ({ ...prev, data: res.data, loading: false }));
    } catch {
      setHistoryModal(prev => ({ ...prev, loading: false }));
      toast.error("Erreur lors du chargement de l'historique");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, listingsRes, canceledRes, usersRes, disputesRes, ticketsRes, pendingOrdersRes, awaitingConfirmRes, pendingPaymentsRes, reviewsRes, allOrdersRes, pendingValidationRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/listings/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/listings/canceled`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/disputes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/support/tickets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders/pending-delivery`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/orders/awaiting-confirmation`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/orders/pending-payment`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/platform-reviews`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { reviews: [] } })),
        axios.get(`${API}/admin/orders/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { orders: [] } })),
        axios.get(`${API}/payments/orders-pending-validation`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { orders: [] } }))
      ]);
      
      setStats(statsRes.data);
      setPendingListings(listingsRes.data);
      setCanceledListings(canceledRes.data);
      setAllUsers(usersRes.data);
      setDisputes(disputesRes.data);
      setSupportTickets(ticketsRes.data);
      setPendingOrders(pendingOrdersRes.data || []);
      setAwaitingConfirmOrders(awaitingConfirmRes.data || []);
      setPendingPayments(pendingPaymentsRes.data || []);
      setPlatformReviews(reviewsRes.data?.reviews || []);
      setAllOrders(allOrdersRes.data?.orders || []);
      setPendingValidationOrders(pendingValidationRes.data?.orders || []);
      setCommissionRate(statsRes.data.commission_rate?.toString() || '10');
      setSellerCommissionRate(statsRes.data.seller_commission_rate?.toString() || '10');

      // Fetch currency platform data
      try {
        const [cStatsRes, cPendingRes, cAllRes, cOrdersRes, cSettingsRes] = await Promise.all([
          axios.get(`${API}/currency/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/currency/admin/listings/pending`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/currency/admin/listings/all`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/currency/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/currency/settings`)
        ]);
        setCurrencyStats(cStatsRes.data);
        setCurrencyPendingListings(cPendingRes.data || []);
        setCurrencyAllListings(cAllRes.data || []);
        setCurrencyOrders(cOrdersRes.data || []);
        const cs = cSettingsRes.data;
        setCurrencySettings(cs);
      } catch (e) { console.error('Currency data error:', e); }
      
      // Insurance settings
      if (statsRes.data.insurance) {
        setInsuranceBaseCoverage(statsRes.data.insurance.base_coverage?.toString() || '100');
        setInsuranceRate((statsRes.data.insurance.rate * 100)?.toString() || '2');
        setInsuranceClaimDelay(statsRes.data.insurance.claim_delay_hours?.toString() || '48');
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };
  
  const confirmPayment = async (orderId) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/confirm-payment`, {
        payment_reference: paymentReference,
        admin_notes: paymentNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paiement confirmé avec succès');
      setConfirmPaymentDialog(null);
      setPaymentReference('');
      setPaymentNotes('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la confirmation du paiement');
    }
  };
  
  const cancelPendingPayment = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) return;
    try {
      await axios.post(`${API}/admin/orders/${orderId}/cancel-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Commande annulée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const verifyListing = async (listingId) => {
    try {
      await axios.post(`${API}/admin/listings/${listingId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce vérifiée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  // Charger le billet d'une annonce
  const loadTicketFile = async (listingId) => {
    setTicketLoading(true);
    try {
      const res = await axios.get(`${API}/listings/${listingId}/ticket`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      // Create a blob URL from the binary response
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      // Store both URL and content type for proper rendering
      setViewingTicket({ url: blobUrl, type: contentType });
    } catch (error) {
      // Fallback: try JSON response (legacy base64 format)
      try {
        const res = await axios.get(`${API}/listings/${listingId}/ticket`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.ticket_file) {
          setViewingTicket({ url: res.data.ticket_file, type: 'legacy' });
        } else {
          toast.error('Aucun billet trouvé');
          setViewingTicket(null);
        }
      } catch {
        toast.error('Erreur lors du chargement du billet');
        setViewingTicket(null);
      }
    } finally {
      setTicketLoading(false);
    }
  };

  const rejectListing = async (listingId) => {
    try {
      await axios.post(`${API}/admin/listings/${listingId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Annonce rejetée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  // Fonction pour mettre à jour le statut d'une commande (logistique centralisée)
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Statut mis à jour: ${ORDER_STATUSES[newStatus]}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du statut');
    }
  };

  // Fonction pour exécuter le job de confirmation automatique 48h
  const runAutoConfirmJob = async () => {
    try {
      const res = await axios.post(`${API}/admin/jobs/auto-confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Job exécuté: ${res.data.orders_processed} commande(s) traitée(s)`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'exécution du job');
    }
  };

  const updateCommission = async () => {
    try {
      await axios.put(`${API}/admin/commission`, { rate: parseFloat(commissionRate) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Commission acheteur mise à jour : ${commissionRate}%`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const updateSellerCommission = async () => {
    try {
      await axios.put(`${API}/admin/seller-commission`, { rate: parseFloat(sellerCommissionRate) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Commission vendeur mise à jour : ${sellerCommissionRate}%`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Insurance settings update functions
  const updateInsuranceBaseCoverage = async () => {
    try {
      await axios.put(`${API}/admin/insurance/base-coverage`, { amount: parseFloat(insuranceBaseCoverage) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Couverture de base mise à jour : ${insuranceBaseCoverage} CAD`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const updateInsuranceRate = async () => {
    try {
      await axios.put(`${API}/admin/insurance/rate`, { rate: parseFloat(insuranceRate) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Taux d'assurance mis à jour : ${insuranceRate}%`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const updateInsuranceClaimDelay = async () => {
    try {
      await axios.put(`${API}/admin/insurance/claim-delay`, { hours: parseInt(insuranceClaimDelay) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Délai de réclamation mis à jour : ${insuranceClaimDelay} heures`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const makeAdmin = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/make-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utilisateur promu admin');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utilisateur supprimé');
      setDeleteUserConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Dispute management
  const resolveDispute = async (disputeId, resolution, notes) => {
    try {
      await axios.post(`${API}/admin/disputes/${disputeId}/resolve`, {
        resolution,
        admin_notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Litige résolu');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la résolution');
    }
  };

  const addDisputeMessage = async (disputeId, message) => {
    try {
      await axios.post(`${API}/admin/disputes/${disputeId}/message`, { message }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message envoyé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    }
  };

  // Support ticket management
  const replyToTicket = async (ticketId, message) => {
    try {
      await axios.post(`${API}/admin/support/tickets/${ticketId}/reply`, { message }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Réponse envoyée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const closeTicket = async (ticketId) => {
    try {
      await axios.post(`${API}/admin/support/tickets/${ticketId}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket fermé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la fermeture');
    }
  };

  // Filters
  const filteredDisputes = disputes.filter(d => 
    disputeFilter === 'all' || d.status === disputeFilter
  );
  
  const filteredTickets = supportTickets.filter(t => 
    ticketFilter === 'all' || t.status === ticketFilter
  );

  const openDisputesCount = disputes.filter(d => d.status === 'open').length;
  const openTicketsCount = supportTickets.filter(t => t.status !== 'closed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold">Administration</h1>
            <p className="text-muted-foreground">Gerez la plateforme Kacos</p>
          </div>
          <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
            <ShieldCheck className="w-4 h-4 mr-1" />
            Admin
          </Badge>
        </div>

        {/* === TOP-LEVEL SECTION SELECTOR === */}
        {adminSection === 'overview' ? (
          <div className="space-y-8">
            {/* Global Quick Stats */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="text-xl font-bold">{stats?.users?.total || 0}</div>
                <div className="text-xs text-muted-foreground">Utilisateurs</div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="text-xl font-bold">{stats?.listings?.active || 0}</div>
                <div className="text-xs text-muted-foreground">Annonces bagages</div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="text-xl font-bold">{currencyStats?.listings?.active || 0}</div>
                <div className="text-xs text-muted-foreground">Transactions devises</div>
              </div>
            </div>

            {/* 3 Big Section Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="admin-sections">
              {/* BAGAGES */}
              <Card
                className="group cursor-pointer border-2 border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 overflow-hidden"
                onClick={() => { setAdminSection('baggage'); setActiveTab('pending-validation'); }}
                data-testid="admin-section-baggage"
              >
                <div className="h-3 bg-gradient-to-r from-amber-600 to-yellow-500 w-full" />
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Luggage className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-xl font-bold">Bagages</h2>
                  <p className="text-sm text-muted-foreground">
                    Annonces, commandes, paiements, logistique et parametres de la plateforme bagages
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {(stats?.listings?.pending_verification || 0) > 0 && (
                      <Badge className="bg-amber-500/20 text-amber-600">
                        {stats.listings.pending_verification} annonces a verifier
                      </Badge>
                    )}
                    {pendingValidationOrders.length > 0 && (
                      <Badge className="bg-primary/20 text-primary">
                        {pendingValidationOrders.length} commandes en attente
                      </Badge>
                    )}
                    {openDisputesCount > 0 && (
                      <Badge className="bg-red-500/20 text-red-500">
                        {openDisputesCount} litiges
                      </Badge>
                    )}
                    {(stats?.listings?.pending_verification || 0) === 0 && pendingValidationOrders.length === 0 && openDisputesCount === 0 && (
                      <Badge className="bg-emerald-500/20 text-emerald-500">Tout est en ordre</Badge>
                    )}
                  </div>
                  <Button className="w-full mt-2" variant="outline">
                    <Luggage className="w-4 h-4 mr-2" /> Acceder
                  </Button>
                </CardContent>
              </Card>

              {/* DEVISES */}
              <Card
                className="group cursor-pointer border-2 border-border/50 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/5 overflow-hidden"
                onClick={() => setAdminSection('currency')}
                data-testid="admin-section-currency"
              >
                <div className="h-3 bg-gradient-to-r from-emerald-600 to-teal-500 w-full" />
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <DollarSign className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h2 className="text-xl font-bold">Devises</h2>
                  <p className="text-sm text-muted-foreground">
                    Annonces de devises, commandes, parametres de prix et commissions
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {currencyPendingListings.length > 0 && (
                      <Badge className="bg-amber-500/20 text-amber-600">
                        {currencyPendingListings.length} annonces a valider
                      </Badge>
                    )}
                    {(currencyStats?.orders?.pending || 0) > 0 && (
                      <Badge className="bg-primary/20 text-primary">
                        {currencyStats.orders.pending} commandes en attente
                      </Badge>
                    )}
                    {currencyPendingListings.length === 0 && !(currencyStats?.orders?.pending > 0) && (
                      <Badge className="bg-emerald-500/20 text-emerald-500">Tout est en ordre</Badge>
                    )}
                  </div>
                  <Button className="w-full mt-2" variant="outline">
                    <DollarSign className="w-4 h-4 mr-2" /> Acceder
                  </Button>
                </CardContent>
              </Card>

              {/* UTILISATEURS */}
              <Card
                className="group cursor-pointer border-2 border-border/50 hover:border-violet-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-500/5 overflow-hidden"
                onClick={() => { setAdminSection('users'); setActiveTab('users'); }}
                data-testid="admin-section-users"
              >
                <div className="h-3 bg-gradient-to-r from-violet-600 to-fuchsia-500 w-full" />
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-violet-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Users className="w-8 h-8 text-violet-500" />
                  </div>
                  <h2 className="text-xl font-bold">Utilisateurs</h2>
                  <p className="text-sm text-muted-foreground">
                    Gestion des comptes, verifications, grades et scores des utilisateurs
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <Badge className="bg-violet-500/20 text-violet-500">
                      {allUsers.length} utilisateurs
                    </Badge>
                    <Badge className="bg-emerald-500/20 text-emerald-500">
                      {stats?.users?.verified || 0} verifies
                    </Badge>
                  </div>
                  <Button className="w-full mt-2" variant="outline">
                    <Users className="w-4 h-4 mr-2" /> Acceder
                  </Button>
                </CardContent>
              </Card>

              {/* AVIS */}
              <Card
                className="group cursor-pointer border-2 border-border/50 hover:border-yellow-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-yellow-500/5 overflow-hidden"
                onClick={() => setAdminSection('reviews')}
                data-testid="admin-section-reviews"
              >
                <div className="h-3 bg-gradient-to-r from-yellow-500 to-orange-500 w-full" />
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-yellow-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Star className="w-8 h-8 text-yellow-500" />
                  </div>
                  <h2 className="text-xl font-bold">Avis</h2>
                  <p className="text-sm text-muted-foreground">
                    Gestion des avis de la plateforme et selection des avis a afficher
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <Badge className="bg-yellow-500/20 text-yellow-600">
                      {platformReviews.length} avis
                    </Badge>
                    <Badge className="bg-yellow-500/20 text-yellow-600">
                      {platformReviews.filter(r => r.featured).length}/5 affiches
                    </Badge>
                  </div>
                  <Button className="w-full mt-2" variant="outline">
                    <Star className="w-4 h-4 mr-2" /> Acceder
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <>
            {/* Back button + section title */}
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" size="icon" onClick={() => setAdminSection('overview')} data-testid="back-to-admin-overview">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                {adminSection === 'baggage' && <Luggage className="w-6 h-6 text-primary" />}
                {adminSection === 'currency' && <DollarSign className="w-6 h-6 text-emerald-500" />}
                {adminSection === 'users' && <Users className="w-6 h-6 text-violet-500" />}
                {adminSection === 'reviews' && <Star className="w-6 h-6 text-yellow-500" />}
                <h2 className="text-xl font-bold">
                  {adminSection === 'baggage' && 'Bagages'}
                  {adminSection === 'currency' && 'Devises'}
                  {adminSection === 'users' && 'Utilisateurs'}
                  {adminSection === 'reviews' && 'Avis'}
                </h2>
              </div>
              {/* Quick section switcher */}
              <div className="ml-auto flex gap-2">
                <Button variant={adminSection === 'baggage' ? 'default' : 'outline'} size="sm"
                  onClick={() => { setAdminSection('baggage'); setActiveTab('pending-validation'); }}
                  data-testid="switch-baggage">
                  <Luggage className="w-4 h-4 mr-1" /> Bagages
                </Button>
                <Button variant={adminSection === 'currency' ? 'default' : 'outline'} size="sm"
                  className={adminSection === 'currency' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={() => setAdminSection('currency')}
                  data-testid="switch-currency">
                  <DollarSign className="w-4 h-4 mr-1" /> Devises
                </Button>
                <Button variant={adminSection === 'users' ? 'default' : 'outline'} size="sm"
                  className={adminSection === 'users' ? 'bg-violet-600 hover:bg-violet-700' : ''}
                  onClick={() => { setAdminSection('users'); setActiveTab('users'); }}
                  data-testid="switch-users">
                  <Users className="w-4 h-4 mr-1" /> Utilisateurs
                </Button>
                <Button variant={adminSection === 'reviews' ? 'default' : 'outline'} size="sm"
                  className={adminSection === 'reviews' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}
                  onClick={() => setAdminSection('reviews')}
                  data-testid="switch-reviews">
                  <Star className="w-4 h-4 mr-1" /> Avis
                </Button>
              </div>
            </div>

        {/* ==================== BAGGAGE SECTION ==================== */}
        {adminSection === 'baggage' && (
          <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Package className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.listings?.pending_verification || 0}</p>
                  <p className="text-sm text-muted-foreground">A verifier</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{openDisputesCount}</p>
                  <p className="text-sm text-muted-foreground">Litiges ouverts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                  <Headphones className="w-6 h-6 text-violet-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{openTicketsCount}</p>
                  <p className="text-sm text-muted-foreground">Tickets support</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats?.revenue?.total_commission?.toFixed(2) || '0.00'} CAD
                  </p>
                  <p className="text-sm text-muted-foreground">Commissions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Commission Settings */}
        <Card className="card-dashboard mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-primary" />
              Paramètres de commission
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Commission Acheteur */}
            <div>
              <p className="text-sm font-medium mb-2">Commission Acheteur (ajoutée au prix)</p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  className="w-24"
                  min="0"
                  max="100"
                  step="0.5"
                  data-testid="commission-input"
                />
                <span className="text-muted-foreground">%</span>
                <Button onClick={updateCommission} data-testid="update-commission-btn">
                  Mettre à jour
                </Button>
              </div>
            </div>
            
            {/* Commission Vendeur */}
            <div>
              <p className="text-sm font-medium mb-2">Commission Vendeur (prélevée sur le paiement)</p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={sellerCommissionRate}
                  onChange={(e) => setSellerCommissionRate(e.target.value)}
                  className="w-24"
                  min="0"
                  max="100"
                  step="0.5"
                  data-testid="seller-commission-input"
                />
                <span className="text-muted-foreground">%</span>
                <Button onClick={updateSellerCommission} data-testid="update-seller-commission-btn">
                  Mettre à jour
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Cette commission est visible uniquement par le vendeur
              </p>
            </div>
            
            {/* Volume */}
            <p className="text-sm text-muted-foreground pt-2 border-t">
              Volume total: {stats?.revenue?.total_volume?.toFixed(2) || '0.00'} CAD
            </p>
          </CardContent>
        </Card>

        {/* Insurance Settings */}
        <Card className="card-dashboard mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Paramètres d'assurance transport
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Couverture de base */}
            <div>
              <p className="text-sm font-medium mb-2">Couverture de base gratuite (CAD)</p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={insuranceBaseCoverage}
                  onChange={(e) => setInsuranceBaseCoverage(e.target.value)}
                  className="w-32"
                  min="0"
                  step="10"
                  data-testid="insurance-base-coverage-input"
                />
                <span className="text-muted-foreground">CAD</span>
                <Button onClick={updateInsuranceBaseCoverage} data-testid="update-insurance-base-btn">
                  Mettre à jour
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Montant couvert gratuitement pour chaque colis
              </p>
            </div>
            
            {/* Taux d'assurance */}
            <div>
              <p className="text-sm font-medium mb-2">Taux d'assurance (% sur la valeur excédentaire)</p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={insuranceRate}
                  onChange={(e) => setInsuranceRate(e.target.value)}
                  className="w-24"
                  min="0"
                  max="100"
                  step="0.5"
                  data-testid="insurance-rate-input"
                />
                <span className="text-muted-foreground">%</span>
                <Button onClick={updateInsuranceRate} data-testid="update-insurance-rate-btn">
                  Mettre à jour
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Taux appliqué sur le montant excédant la couverture de base
              </p>
            </div>
            
            {/* Délai de réclamation */}
            <div>
              <p className="text-sm font-medium mb-2">Délai de réclamation (heures)</p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={insuranceClaimDelay}
                  onChange={(e) => setInsuranceClaimDelay(e.target.value)}
                  className="w-24"
                  min="0"
                  step="1"
                  data-testid="insurance-claim-delay-input"
                />
                <span className="text-muted-foreground">heures</span>
                <Button onClick={updateInsuranceClaimDelay} data-testid="update-insurance-delay-btn">
                  Mettre à jour
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Délai maximum après réception pour soumettre une réclamation
              </p>
            </div>
            
            {/* Example calculation */}
            <div className="bg-primary/10 p-3 rounded-lg text-sm">
              <p className="font-medium text-primary mb-2">Exemple de calcul :</p>
              <p className="text-muted-foreground">
                Pour un colis déclaré à 500 CAD avec couverture de base {insuranceBaseCoverage || 100} CAD :
              </p>
              <p className="text-primary">
                Coût d'assurance = (500 - {insuranceBaseCoverage || 100}) × {insuranceRate || 2}% = {((500 - (parseFloat(insuranceBaseCoverage) || 100)) * ((parseFloat(insuranceRate) || 2) / 100)).toFixed(2)} CAD
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="pending-validation" data-testid="tab-pending-validation">
              Commandes à valider
              {pendingValidationOrders.length > 0 && (
                <Badge className="ml-2 bg-amber-500 text-white animate-pulse">
                  {pendingValidationOrders.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">
              <Banknote className="w-4 h-4 mr-1" />Paiements
              {pendingPayments.length > 0 && (
                <Badge className="ml-2 bg-yellow-500 text-white">
                  {pendingPayments.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="logistics" data-testid="tab-logistics">
              <Truck className="w-4 h-4 mr-1" />Logistique
              {pendingOrders.length > 0 && (
                <Badge className="ml-2 bg-teal-500 text-white">
                  {pendingOrders.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support">
              Support
              {openTicketsCount > 0 && (
                <Badge className="ml-2 bg-violet-500 text-white">
                  {openTicketsCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="disputes" data-testid="tab-disputes">
              Litiges
              {openDisputesCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">
                  {openDisputesCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all-listings" data-testid="tab-all-listings">
              <Package className="w-4 h-4 mr-1" />Toutes Annonces
              {pendingListings.length > 0 && (
                <Badge className="ml-2 bg-orange-500 text-white animate-pulse">
                  {pendingListings.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all-orders" data-testid="tab-all-orders">
              Toutes commandes ({allOrders.length})
            </TabsTrigger>
            <TabsTrigger value="canceled" data-testid="tab-canceled-listings">
              Annulees ({canceledListings.length})
            </TabsTrigger>
          </TabsList>

          {/* PAYMENTS TAB */}
          <TabsContent value="payments" className="space-y-4">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Banknote className="w-5 h-5" />
                  Paiements en attente de confirmation
                </CardTitle>
                <CardDescription>
                  Toutes les commandes validées en attente de paiement
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingPayments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p>Aucun paiement en attente</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingPayments.map((order) => {
                      const PaymentIcon = PAYMENT_METHOD_ICONS[order.payment_method] || Banknote;
                      const hoursRemaining = order.hours_remaining || 0;
                      const isExpired = order.is_expired;
                      
                      return (
                        <Card key={order.id} className={`border ${isExpired ? 'border-red-500/50 bg-red-500/5' : 'border-yellow-500/30'}`}>
                          <CardContent className="py-4">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <PaymentIcon className="w-5 h-5" />
                                  <span className="font-bold text-lg">
                                    {order.total?.toFixed(2)} {order.currency || 'CAD'}
                                  </span>
                                  <Badge className={isExpired ? 'bg-red-500' : 'bg-yellow-500'}>
                                    {PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method}
                                  </Badge>
                                </div>
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Acheteur:</span> {order.buyer_name} ({order.buyer_email})
                                </p>
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Matricule:</span> 
                                  <code className="ml-1 bg-muted px-1 rounded">{order.buyer_matricule}</code>
                                </p>
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Trajet:</span> {flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Commande: {order.id?.slice(0, 8).toUpperCase()} • 
                                  {new Date(order.created_at).toLocaleString('fr-CA')}
                                </p>
                                {order.listing_id && (
                                  <Link to={`/listings/${order.listing_id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1 mt-1">
                                    <ExternalLink className="w-3 h-3" />Voir l'annonce
                                  </Link>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <Clock className={`w-4 h-4 ${isExpired ? 'text-red-500' : 'text-yellow-500'}`} />
                                  <span className={`text-sm ${isExpired ? 'text-red-500 font-bold' : 'text-yellow-600'}`}>
                                    {isExpired 
                                      ? 'Délai expiré' 
                                      : `${hoursRemaining.toFixed(1)}h restantes`
                                    }
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => setViewingOrderDetail(order)}
                                  data-testid={`view-payment-detail-${order.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Détails
                                </Button>
                                <Button
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => setConfirmPaymentDialog(order)}
                                  data-testid={`confirm-payment-${order.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Confirmer paiement
                                </Button>
                                <Button
                                  variant="outline"
                                  className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                  onClick={() => cancelPendingPayment(order.id)}
                                  data-testid={`cancel-payment-${order.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Annuler
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const users = [];
                                    if (order.buyer_email) users.push({ id: order.buyer_id, name: order.buyer_name || 'Acheteur', email: order.buyer_email, role: 'buyer' });
                                    if (order.seller_email) users.push({ id: order.seller_id, name: order.seller_name || 'Vendeur', email: order.seller_email, role: 'seller' });
                                    setContactModal({ open: true, users, contextType: 'payment', contextId: order.id });
                                  }}
                                  data-testid={`contact-payment-${order.id}`}
                                >
                                  <Mail className="w-4 h-4 mr-1" />Contacter
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ALL LISTINGS TAB */}
          <TabsContent value="all-listings" className="space-y-4">
            <AdminListingsManager onDataChange={fetchData} initialSellerFilter={listingSellerFilter} />
          </TabsContent>

          {/* DISPUTES TAB */}
          <TabsContent value="disputes" className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <Select value={disputeFilter} onValueChange={setDisputeFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les litiges</SelectItem>
                  <SelectItem value="open">Ouverts</SelectItem>
                  <SelectItem value="resolved">Résolus</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                {filteredDisputes.length} litige{filteredDisputes.length > 1 ? 's' : ''}
              </span>
            </div>

            {filteredDisputes.length === 0 ? (
              <Card className="card-dashboard">
                <CardContent className="py-12 text-center">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Aucun litige</p>
                </CardContent>
              </Card>
            ) : (
              filteredDisputes.map((dispute) => (
                <div key={dispute.id} className="relative">
                  <DisputeCard 
                    dispute={dispute}
                    onResolve={resolveDispute}
                    onMessage={addDisputeMessage}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-4 right-4"
                    onClick={() => {
                      const users = [];
                      if (dispute.buyer_email) users.push({ id: dispute.buyer_id, name: dispute.buyer_name || 'Acheteur', email: dispute.buyer_email, role: 'buyer' });
                      if (dispute.seller_email) users.push({ id: dispute.seller_id, name: dispute.seller_name || 'Vendeur', email: dispute.seller_email, role: 'seller' });
                      setContactModal({ open: true, users, contextType: 'dispute', contextId: dispute.id });
                    }}
                    data-testid={`contact-dispute-${dispute.id}`}
                  >
                    <Mail className="w-4 h-4 mr-1" />Contacter
                  </Button>
                </div>
              ))
            )}
          </TabsContent>

          {/* SUPPORT TAB */}
          <TabsContent value="support" className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <Select value={ticketFilter} onValueChange={setTicketFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les tickets</SelectItem>
                  <SelectItem value="open">Ouverts</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="closed">Fermés</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                {filteredTickets.length} ticket{filteredTickets.length > 1 ? 's' : ''}
              </span>
            </div>

            {filteredTickets.length === 0 ? (
              <Card className="card-dashboard">
                <CardContent className="py-12 text-center">
                  <Headphones className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Aucun ticket support</p>
                </CardContent>
              </Card>
            ) : (
              filteredTickets.map((ticket) => (
                <SupportTicketCard 
                  key={ticket.id} 
                  ticket={ticket}
                  onReply={replyToTicket}
                  onClose={closeTicket}
                />
              ))
            )}
          </TabsContent>

          {/* CANCELED LISTINGS TAB */}
          <TabsContent value="canceled" className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Archive className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {canceledListings.length} annonce{canceledListings.length > 1 ? 's' : ''} annulée{canceledListings.length > 1 ? 's' : ''}
              </span>
            </div>

            {canceledListings.length === 0 ? (
              <Card className="card-dashboard">
                <CardContent className="py-12 text-center">
                  <Archive className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Aucune annonce annulée</p>
                </CardContent>
              </Card>
            ) : (
              canceledListings.map((canceled) => {
                const listing = canceled.original_listing_data || {};
                return (
                  <Card key={canceled.id} className="card-dashboard" data-testid={`canceled-listing-${canceled.id}`}>
                    <CardContent className="py-4">
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Plane className="w-5 h-5 text-muted-foreground" />
                            <span className="font-medium">
                              {flag(listing.departure_country)} {listing.departure_city || 'N/A'} → {flag(listing.destination_country)} {listing.destination_city || 'N/A'}
                            </span>
                            <Badge className="bg-gray-500/20 text-gray-500">Annulée</Badge>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-2">
                            <strong>Raison:</strong> {canceled.reason}
                          </p>
                          
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(canceled.canceled_at).toLocaleDateString('fr-FR')}
                            </span>
                            <span>•</span>
                            <span>Par: {canceled.canceled_by_name} ({canceled.canceled_by_role === 'admin' ? 'Admin' : 'Vendeur'})</span>
                            <span>•</span>
                            <span>{canceled.orders_count || 0} commande(s) remboursée(s)</span>
                            {canceled.total_refunded > 0 && (
                              <span className="text-amber-500">• {canceled.total_refunded.toFixed(2)} CAD</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* LOGISTICS TAB - Gestion des commandes */}
          <TabsContent value="logistics" className="space-y-6">
            {/* Bouton pour exécuter le job auto-confirm */}
            <Card className="border-orange-500/30 bg-orange-500/5">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Timer className="w-6 h-6 text-orange-500" />
                    <div>
                      <p className="font-medium">Job de confirmation automatique (48h)</p>
                      <p className="text-sm text-muted-foreground">
                        Confirme automatiquement les commandes livrées depuis plus de 48h sans litige
                      </p>
                    </div>
                  </div>
                  <Button onClick={runAutoConfirmJob} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                    <Timer className="w-4 h-4 mr-2" />Exécuter maintenant
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Section: Commandes en attente de confirmation (48h) */}
            {awaitingConfirmOrders.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-500" />
                  En attente de confirmation ({awaitingConfirmOrders.length})
                </h3>
                <div className="space-y-3">
                  {awaitingConfirmOrders.map((order) => (
                    <Card key={order.id} className="border-green-500/30">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-green-500/20 text-green-500">Livré</Badge>
                              <span className="text-sm text-muted-foreground">#{order.id.slice(0, 8)}</span>
                            </div>
                            <p className="font-medium">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</p>
                            <p className="text-sm text-muted-foreground">
                              Acheteur: <span className="font-medium text-foreground">{order.buyer_name || 'N/A'}</span> • Vendeur: <span className="font-medium text-foreground">{order.seller_name || 'N/A'}</span>
                            </p>
                            {order.listing_id && (
                              <Link to={`/listings/${order.listing_id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1 mt-1">
                                <ExternalLink className="w-3 h-3" />Voir l'annonce
                              </Link>
                            )}
                            {order.auto_confirm_in_hours !== undefined && (
                              <p className="text-sm text-orange-500 mt-1">
                                <Timer className="w-3 h-3 inline mr-1" />
                                Auto-confirmation dans {order.auto_confirm_in_hours.toFixed(1)}h
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-500">{order.total?.toFixed(2)} CAD</p>
                            <p className="text-xs text-muted-foreground">Vendeur: {order.seller_amount?.toFixed(2)} CAD</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Section: Commandes en cours de traitement */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-500" />
                Commandes à traiter ({pendingOrders.length})
              </h3>
              
              {pendingOrders.length === 0 ? (
                <Card className="card-dashboard">
                  <CardContent className="py-12 text-center">
                    <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Aucune commande en attente</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {pendingOrders.map((order) => (
                    <Card key={order.id} className="card-dashboard">
                      <CardContent className="py-4">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={STATUS_COLORS[order.current_status] || 'bg-gray-500/20 text-gray-500'}>
                                {ORDER_STATUSES[order.current_status] || order.current_status}
                              </Badge>
                              <span className="text-sm text-muted-foreground">#{order.id.slice(0, 8)}</span>
                              <span className="text-sm text-muted-foreground">
                                {new Date(order.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            <p className="font-medium text-lg">
                              <MapPin className="w-4 h-4 inline mr-1 text-primary" />
                              {flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}
                            </p>
                            <div className="text-sm text-muted-foreground mt-1">
                              <p>Acheteur: <span className="text-foreground font-medium">{order.buyer_name || 'N/A'}</span> <span className="font-mono text-xs">({order.buyer_email || ''})</span></p>
                              <p>Vendeur: <span className="text-foreground font-medium">{order.seller_name || 'N/A'}</span> <span className="font-mono text-xs">({order.seller_email || ''})</span></p>
                              <p>{formatOrderBaggage(order)}</p>
                              {order.listing_id && (
                                <Link to={`/listings/${order.listing_id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1 mt-1">
                                  <ExternalLink className="w-3 h-3" />Voir l'annonce
                                </Link>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <div className="text-right mb-2">
                              <p className="text-xl font-bold">{order.total?.toFixed(2)} CAD</p>
                              <p className="text-xs text-muted-foreground">Vendeur: {order.seller_amount?.toFixed(2)} CAD</p>
                            </div>
                            
                            {/* Boutons de progression de statut */}
                            <div className="flex flex-wrap gap-2 justify-end">
                              {order.current_status === 'paid' && (
                                <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.id, 'awaiting_pickup_seller')}>
                                  <Clock className="w-4 h-4 mr-1" />Validation vendeur
                                </Button>
                              )}
                              {order.current_status === 'awaiting_pickup_seller' && (
                                <Button size="sm" onClick={() => updateOrderStatus(order.id, 'received_admin_departure')}>
                                  <CheckCircle className="w-4 h-4 mr-1" />Colis reçu (départ)
                                </Button>
                              )}
                              {order.current_status === 'received_admin_departure' && (
                                <Button size="sm" onClick={() => updateOrderStatus(order.id, 'in_transit')}>
                                  <Truck className="w-4 h-4 mr-1" />En transit
                                </Button>
                              )}
                              {order.current_status === 'in_transit' && (
                                <Button size="sm" onClick={() => updateOrderStatus(order.id, 'received_admin_destination')}>
                                  <MapPin className="w-4 h-4 mr-1" />Reçu (destination)
                                </Button>
                              )}
                              {order.current_status === 'received_admin_destination' && (
                                <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => updateOrderStatus(order.id, 'delivered')}>
                                  <CheckCircle className="w-4 h-4 mr-1" />Marquer livré
                                </Button>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const users = [];
                                if (order.buyer_email) users.push({ id: order.buyer_id, name: order.buyer_name || 'Acheteur', email: order.buyer_email, role: 'buyer' });
                                if (order.seller_email) users.push({ id: order.seller_id, name: order.seller_name || 'Vendeur', email: order.seller_email, role: 'seller' });
                                setContactModal({ open: true, users, contextType: 'logistics', contextId: order.id });
                              }}
                              data-testid={`contact-logistics-${order.id}`}
                            >
                              <Mail className="w-4 h-4 mr-1" />Contacter
                            </Button>
                            {(order.current_status === 'cancelled' || order.current_status === 'rejected' || order.current_status === 'cancelled_by_buyer' || order.current_status === 'cancelled_by_seller') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                onClick={async () => {
                                  if (!window.confirm('Supprimer définitivement cette commande ?')) return;
                                  try {
                                    await axios.delete(`${API}/admin/orders/${order.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success('Commande supprimée');
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Erreur');
                                  }
                                }}
                                data-testid={`delete-order-${order.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />Supprimer
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ALL ORDERS TAB */}
          <TabsContent value="all-orders" className="space-y-4" data-testid="all-orders-tab-content">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Toutes les commandes
                </CardTitle>
                <CardDescription>Liste complète de toutes les commandes avec filtres</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
                    <SelectTrigger className="w-48" data-testid="order-status-filter">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="pending_admin_validation">En attente validation</SelectItem>
                      <SelectItem value="pending_payment">En attente paiement</SelectItem>
                      <SelectItem value="paid">Payé</SelectItem>
                      <SelectItem value="awaiting_pickup_seller">Attente récup. vendeur</SelectItem>
                      <SelectItem value="received_admin_departure">Reçu admin départ</SelectItem>
                      <SelectItem value="in_transit">En transit</SelectItem>
                      <SelectItem value="received_admin_destination">Reçu admin destination</SelectItem>
                      <SelectItem value="delivered">Livré</SelectItem>
                      <SelectItem value="completed">Complété</SelectItem>
                      <SelectItem value="rejected">Refusé</SelectItem>
                      <SelectItem value="cancelled">Annulé</SelectItem>
                      <SelectItem value="refunded">Remboursé</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={orderPaymentFilter} onValueChange={setOrderPaymentFilter}>
                    <SelectTrigger className="w-48" data-testid="order-payment-filter">
                      <SelectValue placeholder="Paiement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les paiements</SelectItem>
                      <SelectItem value="paid">Payé</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="failed">Échoué</SelectItem>
                      <SelectItem value="refunded">Remboursé</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Nom de l'acheteur"
                    value={orderBuyerFilter}
                    onChange={(e) => setOrderBuyerFilter(e.target.value)}
                    className="w-44"
                    data-testid="order-buyer-filter"
                  />
                  <Input
                    placeholder="Nom du vendeur"
                    value={orderSellerFilter}
                    onChange={(e) => setOrderSellerFilter(e.target.value)}
                    className="w-44"
                    data-testid="order-seller-filter"
                  />
                  <Input
                    placeholder="Pays départ"
                    value={orderDepartFilter}
                    onChange={(e) => setOrderDepartFilter(e.target.value)}
                    className="w-36"
                    data-testid="order-depart-filter"
                  />
                  <Input
                    placeholder="Pays arrivée"
                    value={orderDestFilter}
                    onChange={(e) => setOrderDestFilter(e.target.value)}
                    className="w-36"
                    data-testid="order-dest-filter"
                  />
                </div>

                {/* Orders list */}
                {(() => {
                  let filtered = allOrders;
                  if (orderStatusFilter !== 'all') filtered = filtered.filter(o => o.current_status === orderStatusFilter);
                  if (orderPaymentFilter !== 'all') filtered = filtered.filter(o => o.payment_status === orderPaymentFilter);
                  if (orderBuyerFilter.trim()) {
                    const q = normalize(orderBuyerFilter.trim());
                    filtered = filtered.filter(o =>
                      normalize(o.buyer_info?.full_name).includes(q) ||
                      normalize(o.buyer_info?.matricule).includes(q)
                    );
                  }
                  if (orderSellerFilter.trim()) {
                    const q = normalize(orderSellerFilter.trim());
                    filtered = filtered.filter(o =>
                      normalize(o.seller_info?.full_name).includes(q) ||
                      normalize(o.seller_info?.matricule).includes(q)
                    );
                  }
                  if (orderDepartFilter.trim()) {
                    const q = normalize(orderDepartFilter.trim());
                    filtered = filtered.filter(o =>
                      normalize(o.departure_city).includes(q) ||
                      normalize(o.departure_country).includes(q)
                    );
                  }
                  if (orderDestFilter.trim()) {
                    const q = normalize(orderDestFilter.trim());
                    filtered = filtered.filter(o =>
                      normalize(o.destination_city).includes(q) ||
                      normalize(o.destination_country).includes(q)
                    );
                  }
                  if (filtered.length === 0) return <p className="text-center text-muted-foreground py-8">Aucune commande trouvée</p>;
                  return (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground mb-2">{filtered.length} commande(s)</p>
                      {filtered.map(order => (
                        <div key={order.id} className="p-4 border rounded-lg hover:border-primary/30 transition-all" data-testid={`all-order-${order.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Badge className={STATUS_COLORS[order.current_status]}>{ORDER_STATUSES[order.current_status] || order.current_status}</Badge>
                                <Badge variant="outline">{PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method}</Badge>
                                <Badge variant="outline" className={order.payment_status === 'paid' ? 'bg-green-500/10 text-green-600' : order.payment_status === 'failed' ? 'bg-red-500/10 text-red-600' : ''}>{order.payment_status}</Badge>
                              </div>
                              <h4 className="font-medium">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</h4>
                              <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                                <p>Acheteur: {order.buyer_info?.full_name || 'N/A'} ({order.buyer_info?.matricule || 'N/A'})</p>
                                <p>Vendeur: {order.seller_info?.full_name || 'N/A'} ({order.seller_info?.matricule || 'N/A'})</p>
                                <p>Vol: {order.travel_date ? new Date(order.travel_date + 'T12:00:00').toLocaleDateString('fr-FR') : 'N/A'} • {formatOrderBaggage(order)}</p>
                                <p className="inline-flex items-center gap-1">
                                  <Shield className={`w-3 h-3 ${order.insurance_opt_in ? 'text-primary' : 'text-muted-foreground'}`} />
                                  {order.insurance_opt_in
                                    ? <span className="text-primary">Assuré — Valeur : {order.declared_value?.toFixed(2)} {order.currency || 'CAD'}</span>
                                    : <span>Sans assurance{order.declared_value > 0 ? ` — Valeur : ${order.declared_value?.toFixed(2)} ${order.currency || 'CAD'}` : ''}</span>
                                  }
                                </p>
                                {order.listing_id && (
                                  <Link to={`/listings/${order.listing_id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                                    <ExternalLink className="w-3 h-3" />Voir l'annonce
                                  </Link>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              <p className="text-lg font-bold">{order.total?.toFixed(2)} {order.currency || 'CAD'}</p>
                              <div className="flex gap-1">
                                <Button variant="outline" size="sm" onClick={() => setViewingOrderDetail(order)} data-testid={`all-order-detail-${order.id}`}>
                                  <Eye className="w-4 h-4 mr-1" />Détails
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => {
                                  const users = [];
                                  if (order.buyer_info?.email) users.push({ id: order.buyer_id, name: order.buyer_info.full_name, email: order.buyer_info.email, role: 'buyer' });
                                  if (order.seller_info?.email) users.push({ id: order.seller_id, name: order.seller_info.full_name, email: order.seller_info.email, role: 'seller' });
                                  setContactModal({ open: true, users, contextType: 'order', contextId: order.id });
                                }} data-testid={`all-order-contact-${order.id}`}>
                                  <Mail className="w-4 h-4" />
                                </Button>
                                {(order.current_status === 'cancelled' || order.current_status === 'rejected' || order.current_status === 'cancelled_by_buyer' || order.current_status === 'cancelled_by_seller') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                    onClick={async () => {
                                      if (!window.confirm('Supprimer définitivement cette commande ?')) return;
                                      try {
                                        await axios.delete(`${API}/admin/orders/${order.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success('Commande supprimée');
                                        fetchData();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Erreur');
                                      }
                                    }}
                                    data-testid={`delete-order-${order.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PENDING VALIDATION TAB */}
          <TabsContent value="pending-validation" className="space-y-4" data-testid="pending-validation-tab-content">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Commandes à valider
                </CardTitle>
                <CardDescription>Validez ou refusez les commandes avant que l'acheteur puisse procéder au paiement</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingValidationOrders.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucune commande en attente de validation</p>
                ) : (
                  <div className="space-y-4">
                    {pendingValidationOrders.map(order => (
                      <div key={order.id} className="p-4 border border-amber-500/30 rounded-lg bg-amber-500/5" data-testid={`validation-order-${order.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className="bg-amber-500/20 text-amber-600">En attente de validation</Badge>
                              <Badge variant="outline">{PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method}</Badge>
                              <span className="text-xs text-muted-foreground">#{order.id?.substring(0, 8).toUpperCase()}</span>
                            </div>
                            <h4 className="font-medium">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</h4>
                            <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                              <p>Acheteur: {order.buyer_info?.full_name || order.buyer_name || 'N/A'} ({order.buyer_info?.matricule || order.buyer_matricule || 'N/A'})</p>
                              <p>Vendeur: {order.seller_info?.full_name || order.seller_name || 'N/A'} ({order.seller_info?.matricule || 'N/A'})</p>
                              <p>Vol: {order.travel_date ? new Date(order.travel_date + 'T12:00:00').toLocaleDateString('fr-FR') : 'N/A'} • {formatOrderBaggage(order)}</p>
                              {order.article_description && <p>Description: {order.article_description}</p>}
                              {order.delivery_instructions && <p>Consignes: {order.delivery_instructions}</p>}
                              <p className="text-xs">Soumise le {new Date(order.created_at).toLocaleString('fr-FR')}</p>
                              {order.listing_id && (
                                <Link to={`/listings/${order.listing_id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1 mt-1">
                                  <ExternalLink className="w-3 h-3" />Voir l'annonce
                                </Link>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <p className="text-lg font-bold">{order.total?.toFixed(2)} {order.currency || 'CAD'}</p>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/payments/validate-order/${order.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success('Commande validée ! L\'acheteur a été notifié.');
                                    setPendingValidationOrders(prev => prev.filter(o => o.id !== order.id));
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Erreur');
                                  }
                                }}
                                data-testid={`validate-order-${order.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />Valider
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setRejectModal({ open: true, orderId: order.id, reason: '' })}
                                data-testid={`reject-order-${order.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />Refuser
                              </Button>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => setViewingOrderDetail(order)} data-testid={`validation-detail-${order.id}`}>
                                <Eye className="w-4 h-4 mr-1" />Détails
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => {
                                const users = [];
                                if (order.buyer_info?.email || order.buyer_email) users.push({ id: order.buyer_id, name: order.buyer_info?.full_name || order.buyer_name || 'Acheteur', email: order.buyer_info?.email || order.buyer_email, role: 'buyer' });
                                if (order.seller_info?.email || order.seller_email) users.push({ id: order.seller_id, name: order.seller_info?.full_name || order.seller_name || 'Vendeur', email: order.seller_info?.email || order.seller_email, role: 'seller' });
                                setContactModal({ open: true, users, contextType: 'validation', contextId: order.id });
                              }} data-testid={`contact-validation-${order.id}`}>
                                <Mail className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
          </>
        )}

        {/* ==================== USERS SECTION ==================== */}
        {adminSection === 'users' && (
          <div className="space-y-4">
            {/* Search + Recalculate */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Input
                placeholder="Rechercher par nom, prenom, telephone ou matricule..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="flex-1"
                data-testid="user-search-input"
              />
              <select
                value={userSortBy}
                onChange={(e) => setUserSortBy(e.target.value)}
                className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                data-testid="user-sort-select"
              >
                <option value="date_desc">Date inscription (récent)</option>
                <option value="date_asc">Date inscription (ancien)</option>
                <option value="name_asc">Prénom A → Z</option>
                <option value="name_desc">Prénom Z → A</option>
              </select>
              <Button
                variant="outline"
                className="flex-shrink-0"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/users/recalculate-all-scores`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Scores recalcules pour tous les utilisateurs');
                    fetchData();
                  } catch (error) {
                    toast.error('Erreur lors du recalcul');
                  }
                }}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Recalculer tous les scores
              </Button>
            </div>

            {allUsers.filter(function(u) {
              if (!userSearch.trim()) return true;
              const q = normalize(userSearch.trim());
              const matricule = normalize('KK-' + (u.id || '').substring(0, 8));
              return normalize(u.first_name || '').includes(q)
                || normalize(u.last_name || '').includes(q)
                || normalize((u.first_name || '') + ' ' + (u.last_name || '')).includes(q)
                || normalize(u.phone || '').includes(q)
                || matricule.includes(q);
            }).sort(function(a, b) {
              switch (userSortBy) {
                case 'date_asc':
                  return (a.created_at || '').localeCompare(b.created_at || '');
                case 'name_asc':
                  return (a.first_name || '').localeCompare(b.first_name || '', 'fr');
                case 'name_desc':
                  return (b.first_name || '').localeCompare(a.first_name || '', 'fr');
                case 'date_desc':
                default:
                  return (b.created_at || '').localeCompare(a.created_at || '');
              }
            }).map((u) => (
              <Card key={u.id} className="card-dashboard">
                <CardContent className="py-4">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {u.first_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{u.first_name} {u.last_name}</p>
                            <span className="text-xs text-muted-foreground font-mono">KK-{u.id?.substring(0, 8).toUpperCase()}</span>
                            {u.is_admin && (
                              <Badge className="bg-red-500/20 text-red-500 text-xs">Admin</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{u.email} - {u.phone}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={u.email_verified
                              ? "bg-green-500/20 text-green-500 text-xs"
                              : "bg-red-500/20 text-red-500 text-xs"
                            }>
                              {u.email_verified ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                              Email {u.email_verified ? 'verifie' : 'non verifie'}
                            </Badge>
                            <Badge className={u.phone_verified
                              ? "bg-green-500/20 text-green-500 text-xs"
                              : "bg-red-500/20 text-red-500 text-xs"
                            }>
                              {u.phone_verified ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                              Tel. {u.phone_verified ? 'verifie' : 'non verifie'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewingUserDetail(u)}
                          data-testid={`view-user-detail-${u.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        {!u.is_admin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => makeAdmin(u.id)}
                            data-testid={`make-admin-${u.id}`}
                          >
                            Promouvoir admin
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setContactModal({ open: true, users: [{ id: u.id, name: `${u.first_name} ${u.last_name}`, email: u.email, role: 'user' }], contextType: 'user', contextId: u.id });
                          }}
                          data-testid={`contact-user-${u.id}`}
                        >
                          <Mail className="w-4 h-4 mr-1" />Contacter
                        </Button>
                        {!u.is_admin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => setDeleteUserConfirm({ id: u.id, name: `${u.first_name} ${u.last_name}` })}
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />Supprimer
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <button
                        type="button"
                        className="text-primary hover:underline flex items-center gap-1"
                        onClick={() => {
                          setOrderBuyerFilter((u.first_name || '') + ' ' + (u.last_name || ''));
                          setOrderSellerFilter('');
                          setOrderDepartFilter('');
                          setOrderDestFilter('');
                          setAdminSection('baggage');
                          setActiveTab('all-orders');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        data-testid={`user-orders-link-${u.id}`}
                      >
                        <Package className="w-3 h-3" /> Ses commandes
                      </button>
                      <button
                        type="button"
                        className="text-primary hover:underline flex items-center gap-1"
                        onClick={() => {
                          setListingSellerFilter((u.first_name || '') + ' ' + (u.last_name || ''));
                          setAdminSection('baggage');
                          setActiveTab('all-listings');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        data-testid={`user-listings-link-${u.id}`}
                      >
                        <Plane className="w-3 h-3" /> Ses annonces
                      </button>
                      <button
                        type="button"
                        className="text-emerald-500 hover:underline flex items-center gap-1"
                        onClick={() => {
                          setCurrencyNavOrderBuyer((u.first_name || '') + ' ' + (u.last_name || ''));
                          setCurrencyNavListingSeller(undefined);
                          setCurrencyNavTab('all-orders');
                          setAdminSection('currency');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        data-testid={`user-currency-orders-link-${u.id}`}
                      >
                        <DollarSign className="w-3 h-3" /> Ses transactions devises
                      </button>
                    </div>
                    <div className="border-t pt-4">
                      <UserScoreManager
                        user={u}
                        token={token}
                        API={API}
                        onUpdate={fetchData}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ==================== REVIEWS SECTION ==================== */}
        {adminSection === 'reviews' && (
          <div className="space-y-4">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Gestion des avis plateforme
                </CardTitle>
                <CardDescription>
                  Selectionnez jusqu'a 5 avis a afficher sur la page d'accueil ({platformReviews.filter(r => r.featured).length}/5 selectionnes)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {platformReviews.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucun avis soumis</p>
                ) : (
                  <div className="space-y-3">
                    {platformReviews.map(review => (
                      <div key={review.id} className={`p-4 border rounded-lg flex items-start justify-between gap-4 ${review.featured ? 'border-yellow-500/50 bg-yellow-500/5' : ''}`} data-testid={`admin-review-${review.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{review.user_name}</span>
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map(s => (
                                <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'}`} />
                              ))}
                            </div>
                            {review.featured && (
                              <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30 text-xs">Affiche</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{review.comment}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(review.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant={review.featured ? "default" : "outline"}
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/platform-reviews/${review.id}/toggle-featured`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setPlatformReviews(prev => prev.map(r => r.id === review.id ? { ...r, featured: !r.featured } : r));
                                toast.success(review.featured ? 'Avis retire de la page d\'accueil' : 'Avis ajoute a la page d\'accueil');
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur');
                              }
                            }}
                            data-testid={`toggle-featured-${review.id}`}
                          >
                            <Star className={`w-4 h-4 mr-1 ${review.featured ? 'fill-current' : ''}`} />
                            {review.featured ? 'Retirer' : 'Afficher'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={async () => {
                              if (!window.confirm('Supprimer cet avis ?')) return;
                              try {
                                await axios.delete(`${API}/admin/platform-reviews/${review.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setPlatformReviews(prev => prev.filter(r => r.id !== review.id));
                                toast.success('Avis supprime');
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur');
                              }
                            }}
                            data-testid={`delete-review-${review.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (review.user_id) {
                                const u = allUsers.find(usr => usr.id === review.user_id);
                                const email = u?.email || '';
                                const name = review.user_name || '';
                                if (email) {
                                  setContactModal({ open: true, users: [{ id: review.user_id, name, email, role: 'user' }], contextType: 'review', contextId: review.id });
                                } else {
                                  toast.error('Email de l\'utilisateur non trouve');
                                }
                              }
                            }}
                            data-testid={`contact-review-${review.id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ==================== CURRENCY SECTION ==================== */}
        {adminSection === 'currency' && (
          <AdminCurrencySection
            token={token}
          />
        )}

          </>
        )}

        {/* Reject Order Dialog */}
        <Dialog open={rejectModal.open} onOpenChange={(open) => !open && setRejectModal({ open: false, orderId: '', reason: '' })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Refuser la commande</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">La raison du refus sera envoyée à l'acheteur par email.</p>
              <Textarea
                value={rejectModal.reason}
                onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Raison du refus (obligatoire)..."
                rows={4}
                data-testid="reject-reason-input"
              />
              <Button
                className="w-full"
                variant="destructive"
                disabled={!rejectModal.reason?.trim()}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/payments/reject-order/${rejectModal.orderId}`, { reason: rejectModal.reason.trim() }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success('Commande refusée. L\'acheteur a été notifié.');
                    setPendingValidationOrders(prev => prev.filter(o => o.id !== rejectModal.orderId));
                    setRejectModal({ open: false, orderId: '', reason: '' });
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Erreur');
                  }
                }}
                data-testid="confirm-reject-btn"
              >
                Confirmer le refus
              </Button>
            </div>
          </DialogContent>
        </Dialog>


        {/* Currency Listing History Dialog */}
        <Dialog open={historyModal.open} onOpenChange={(open) => !open && setHistoryModal({ open: false, listingId: '', data: [], loading: false })}>
          <DialogContent className="max-w-lg max-h-[80vh]" data-testid="history-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-primary" />
                Historique des modifications
              </DialogTitle>
              <DialogDescription>Annonce #{historyModal.listingId.slice(0, 8)}</DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[55vh] pr-3">
              {historyModal.loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" />
                </div>
              ) : historyModal.data.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">Aucun historique pour cette annonce</p>
              ) : (
                <div className="relative pl-5">
                  <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-border" />
                  {historyModal.data.map((entry, idx) => {
                    const actionLabels = {
                      created: 'Création',
                      updated: 'Modification',
                      cancelled: 'Annulation',
                      approved: 'Approbation',
                      rejected: 'Refus'
                    };
                    const actionColors = {
                      created: 'bg-primary',
                      updated: 'bg-amber-500',
                      cancelled: 'bg-red-500',
                      approved: 'bg-emerald-500',
                      rejected: 'bg-red-500'
                    };
                    const fieldLabels = {
                      amount_cad: 'Montant (CAD)',
                      price_per_cad_xaf: 'Prix/CAD (FCFA)',
                      description: 'Description',
                      payment_method: 'Mode de paiement',
                      bank_name: 'Banque',
                      bank_account_number: 'N° compte',
                      bank_account_name: 'Titulaire',
                      bank_institution_number: 'N° institution',
                      expiry_date: 'Expiration',
                      status: 'Statut'
                    };
                    const paymentLabels = { interac: 'Virement Interac', cash: 'Comptant', bank_deposit: 'Dépôt bancaire' };
                    const formatVal = (field, val) => {
                      if (val === null || val === undefined) return '—';
                      if (field === 'payment_method') return paymentLabels[val] || val;
                      if (field === 'expiry_date') return new Date(val).toLocaleDateString('fr-FR');
                      if (field === 'description') return val.length > 40 ? val.slice(0, 40) + '...' : val;
                      if (typeof val === 'number') return val.toLocaleString();
                      return String(val);
                    };
                    return (
                      <div key={idx} className="relative mb-5 last:mb-0" data-testid={`history-entry-${idx}`}>
                        <div className={`absolute -left-5 top-1.5 w-3 h-3 rounded-full border-2 border-background ${actionColors[entry.action] || 'bg-muted'}`} />
                        <div className="ml-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] h-5 px-1.5">
                              {actionLabels[entry.action] || entry.action}
                            </Badge>
                            <span className="text-[11px] text-muted-foreground">
                              {new Date(entry.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {entry.needs_reapproval && (
                              <Badge className="bg-amber-500/10 text-amber-600 text-[10px] h-5 px-1.5">Ré-approbation</Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground mb-1.5">par {entry.user_name}</p>
                          {entry.changes && Object.keys(entry.changes).length > 0 && (
                            <div className="bg-muted/40 rounded-md p-2.5 space-y-1">
                              {Object.entries(entry.changes).map(([field, vals]) => (
                                <div key={field} className="flex items-center text-[11px] gap-1.5">
                                  <span className="text-muted-foreground shrink-0">{fieldLabels[field] || field}:</span>
                                  {entry.action === 'created' ? (
                                    <span className="font-medium text-foreground">{formatVal(field, vals.new)}</span>
                                  ) : (
                                    <>
                                      <span className="line-through text-red-400">{formatVal(field, vals.old)}</span>
                                      <span className="text-muted-foreground">→</span>
                                      <span className="font-medium text-emerald-600">{formatVal(field, vals.new)}</span>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>


        <Dialog open={!!confirmPaymentDialog} onOpenChange={(open) => !open && setConfirmPaymentDialog(null)}>

          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmer le paiement</DialogTitle>
              <DialogDescription>
                {confirmPaymentDialog && (
                  <>
                    Commande #{confirmPaymentDialog.id?.slice(0, 8).toUpperCase()} - 
                    {confirmPaymentDialog.total?.toFixed(2)} {confirmPaymentDialog.currency || 'CAD'}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Référence du paiement (optionnel)</Label>
                <Input
                  placeholder="Ex: Numéro de transaction, référence Interac..."
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes admin (optionnel)</Label>
                <Textarea
                  placeholder="Notes internes sur ce paiement..."
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                />
              </div>
              {confirmPaymentDialog && (
                <div className="bg-muted p-3 rounded-lg text-sm">
                  <p><strong>Acheteur:</strong> {confirmPaymentDialog.buyer_name}</p>
                  <p><strong>Matricule:</strong> {confirmPaymentDialog.buyer_matricule}</p>
                  <p><strong>Méthode:</strong> {PAYMENT_METHOD_LABELS[confirmPaymentDialog.payment_method]}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmPaymentDialog(null)}>
                Annuler
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => confirmPayment(confirmPaymentDialog.id)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Confirmer le paiement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Order Detail Modal */}
        <OrderDetailModal
          order={viewingOrderDetail}
          open={!!viewingOrderDetail}
          onClose={() => setViewingOrderDetail(null)}
          isAdmin={true}
        />

        {/* User Detail Modal */}
        <Dialog open={!!viewingUserDetail} onOpenChange={(open) => !open && setViewingUserDetail(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Détails de l'utilisateur
              </DialogTitle>
            </DialogHeader>
            {viewingUserDetail && (
              <div className="space-y-5 pt-2">
                {/* Avatar & Name */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <span className="text-white text-xl font-bold">{viewingUserDetail.first_name?.[0]}{viewingUserDetail.last_name?.[0]}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{viewingUserDetail.first_name} {viewingUserDetail.last_name}</h3>
                    <span className="text-sm font-mono text-muted-foreground">KK-{viewingUserDetail.id?.substring(0, 8).toUpperCase()}</span>
                    <div className="flex gap-2 mt-1">
                      {viewingUserDetail.is_admin && <Badge className="bg-red-500/20 text-red-500">Admin</Badge>}
                      <Badge className="bg-primary/20 text-primary">{viewingUserDetail.grade || 'Nouveau'}</Badge>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <h4 className="text-sm font-semibold mb-2">Informations de contact</h4>
                  <div className="grid grid-cols-[100px_1fr] gap-y-2 text-sm">
                    <span className="text-muted-foreground">Email</span>
                    <span className="font-medium">{viewingUserDetail.email}</span>
                    <span className="text-muted-foreground">Téléphone</span>
                    <span className="font-medium">{viewingUserDetail.phone}</span>
                    <span className="text-muted-foreground">Pays</span>
                    <span className="font-medium">{viewingUserDetail.country || '—'}</span>
                    <span className="text-muted-foreground">Adresse</span>
                    <span className="font-medium">{viewingUserDetail.address || '—'}</span>
                  </div>
                </div>

                {/* Verification Status */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold mb-3">Vérifications de sécurité</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Email</span>
                      <Badge className={viewingUserDetail.email_verified ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}>
                        {viewingUserDetail.email_verified ? <><CheckCircle className="w-3 h-3 mr-1" />Vérifié</> : <><XCircle className="w-3 h-3 mr-1" />Non vérifié</>}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Téléphone</span>
                      <Badge className={viewingUserDetail.phone_verified ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}>
                        {viewingUserDetail.phone_verified ? <><CheckCircle className="w-3 h-3 mr-1" />Vérifié</> : <><XCircle className="w-3 h-3 mr-1" />Non vérifié</>}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Conditions acceptées</span>
                      <Badge className={viewingUserDetail.terms_accepted ? "bg-green-500/20 text-green-500" : "bg-yellow-500/20 text-yellow-500"}>
                        {viewingUserDetail.terms_accepted ? <><CheckCircle className="w-3 h-3 mr-1" />Acceptées</> : <><Clock className="w-3 h-3 mr-1" />En attente</>}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-primary">{viewingUserDetail.total_sales || 0}</p>
                    <p className="text-xs text-muted-foreground">Ventes</p>
                  </div>
                  <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-500">{viewingUserDetail.total_purchases || 0}</p>
                    <p className="text-xs text-muted-foreground">Achats</p>
                  </div>
                  <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-500">{viewingUserDetail.seller_rating?.toFixed(1) || '0.0'}</p>
                    <p className="text-xs text-muted-foreground">Note vendeur</p>
                  </div>
                  <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-violet-500">{viewingUserDetail.buyer_rating?.toFixed(1) || '0.0'}</p>
                    <p className="text-xs text-muted-foreground">Note acheteur</p>
                  </div>
                </div>

                {/* Trust Score & Grade */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="grid grid-cols-[100px_1fr] gap-y-2 text-sm">
                    <span className="text-muted-foreground">Grade</span>
                    <span className="font-medium">{viewingUserDetail.grade || 'Nouveau'}</span>
                    <span className="text-muted-foreground">Score confiance</span>
                    <span className="font-medium">{viewingUserDetail.trust_score?.toFixed(1) || '50.0'} / 100</span>
                    <span className="text-muted-foreground">Litiges</span>
                    <span className="font-medium">{viewingUserDetail.disputes || 0}</span>
                    <span className="text-muted-foreground">Inscrit le</span>
                    <span className="font-medium">{viewingUserDetail.created_at ? new Date(viewingUserDetail.created_at).toLocaleDateString('fr-FR') : '—'}</span>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Contact User Modal */}
        <ContactUserModal
          open={contactModal.open}
          onOpenChange={(open) => setContactModal(prev => ({ ...prev, open }))}
          users={contactModal.users}
          contextType={contactModal.contextType}
          contextId={contactModal.contextId}
          API={API}
          token={token}
        />

        {/* Delete User Confirmation Dialog */}
        <Dialog open={!!deleteUserConfirm} onOpenChange={(open) => !open && setDeleteUserConfirm(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500">
                <Trash2 className="w-5 h-5" /> Supprimer l'utilisateur
              </DialogTitle>
              <DialogDescription>
                Vous êtes sur le point de supprimer définitivement le compte de{' '}
                <strong>{deleteUserConfirm?.name}</strong>.
                Cette action est irréversible et supprimera toutes les données associées.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 flex-row justify-end">
              <Button variant="outline" onClick={() => setDeleteUserConfirm(null)}
                data-testid="cancel-delete-user">
                Annuler
              </Button>
              <Button
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => deleteUser(deleteUserConfirm.id)}
                data-testid="confirm-delete-user"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Confirmer la suppression
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Dispute Card Component
const DisputeCard = ({ dispute, onResolve, onMessage }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [resolution, setResolution] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleResolve = async () => {
    if (!resolution || !adminNotes.trim()) return;
    await onResolve(dispute.id, resolution, adminNotes);
    setShowResolve(false);
    setResolution('');
    setAdminNotes('');
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    await onMessage(dispute.id, newMessage);
    setNewMessage('');
    setSending(false);
  };

  const getResolutionLabel = (res) => {
    switch (res) {
      case 'refund_buyer': return 'Remboursé à l\'acheteur';
      case 'pay_seller': return 'Payé au vendeur';
      case 'partial_refund': return 'Remboursement partiel';
      default: return res;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="card-dashboard" data-testid={`dispute-card-${dispute.id}`}>
      <CardContent className="py-4">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className={`w-5 h-5 ${dispute.status === 'open' ? 'text-red-500' : 'text-green-500'}`} />
              <span className="font-medium">
                {dispute.reason}
              </span>
              <Badge className={dispute.status === 'open' ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}>
                {dispute.status === 'open' ? 'Ouvert' : 'Résolu'}
              </Badge>
            </div>
            
            <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
              {dispute.description}
            </p>
            
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(dispute.created_at)}
              </span>
              <span>•</span>
              <span>Montant: {dispute.escrow_amount?.toFixed(2)} CAD</span>
              <span>•</span>
              <span>Ouvert par: {dispute.opened_by_role === 'buyer' ? 'Acheteur' : 'Vendeur'}</span>
            </div>

            {dispute.status === 'resolved' && (
              <div className="mt-2 p-2 bg-green-500/10 rounded-lg text-sm">
                <p className="font-medium text-green-600">Résolution: {getResolutionLabel(dispute.resolution)}</p>
                {dispute.admin_notes && <p className="text-muted-foreground">{dispute.admin_notes}</p>}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setShowDetails(!showDetails)}
            >
              <Eye className="w-4 h-4 mr-1" />
              Détails
            </Button>
            
            {dispute.status === 'open' && (
              <Dialog open={showResolve} onOpenChange={setShowResolve}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-green-500 hover:bg-green-600">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Résoudre
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Résoudre le litige</DialogTitle>
                    <DialogDescription>
                      Montant en séquestre: {dispute.escrow_amount?.toFixed(2)} CAD
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Décision *</Label>
                      <Select value={resolution} onValueChange={setResolution}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir une résolution" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="refund_buyer">
                            Rembourser l'acheteur intégralement
                          </SelectItem>
                          <SelectItem value="pay_seller">
                            Libérer les fonds au vendeur
                          </SelectItem>
                          <SelectItem value="partial_refund">
                            Remboursement partiel
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Notes de l'administrateur *</Label>
                      <Textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Expliquez la décision..."
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowResolve(false)}>
                      Annuler
                    </Button>
                    <Button 
                      onClick={handleResolve}
                      disabled={!resolution || !adminNotes.trim()}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      Confirmer la résolution
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Details section */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t">
            {/* Order info */}
            {dispute.order && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Détails de la commande</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Trajet:</span>{' '}
                    {flag(dispute.order?.departure_country)} {dispute.order?.departure_city} → {flag(dispute.order?.destination_country)} {dispute.order?.destination_city}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date:</span>{' '}
                    {dispute.order?.travel_date}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Quantité:</span>{' '}
                    {formatOrderBaggage(dispute.order || {quantity: dispute.order?.quantity, baggage_type: 'kilos'})}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total:</span>{' '}
                    {dispute.order?.total?.toFixed(2)} CAD
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="space-y-3">
              <h4 className="font-medium">Conversation</h4>
              <ScrollArea className="h-48 border rounded-lg p-3">
                {dispute.messages?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucun message
                  </p>
                ) : (
                  <div className="space-y-3">
                    {dispute.messages?.map((msg, index) => (
                      <div 
                        key={msg.created_at || index}
                        className={`p-2 rounded-lg text-sm ${
                          msg.sender_role === 'admin' 
                            ? 'bg-primary/10 border border-primary/20' 
                            : 'bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">
                            {msg.sender_name}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {msg.sender_role === 'admin' ? 'Admin' : msg.sender_role === 'buyer' ? 'Acheteur' : 'Vendeur'}
                          </Badge>
                        </div>
                        <p>{msg.content}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(msg.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {dispute.status === 'open' && (
                <div className="flex gap-2">
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Envoyer un message aux parties..."
                    rows={2}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleSendMessage}
                    disabled={sending || !newMessage.trim()}
                  >
                    {sending ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Support Ticket Card Component
const SupportTicketCard = ({ ticket, onReply, onClose }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    setSending(true);
    await onReply(ticket.id, replyMessage);
    setReplyMessage('');
    setSending(false);
  };

  const getSubjectLabel = (subject) => {
    switch (subject) {
      case 'order': return 'Commande';
      case 'listing': return 'Annonce';
      case 'payment': return 'Paiement';
      default: return 'Autre';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-yellow-500/20 text-yellow-500">En attente</Badge>;
      case 'in_progress':
        return <Badge className="bg-primary/20 text-primary">En cours</Badge>;
      case 'closed':
        return <Badge className="bg-green-500/20 text-green-500">Fermé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="card-dashboard" data-testid={`ticket-card-${ticket.id}`}>
      <CardContent className="py-4">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Headphones className="w-5 h-5 text-violet-500" />
              <span className="font-medium">{getSubjectLabel(ticket.subject)}</span>
              {getStatusBadge(ticket.status)}
              {ticket.reference_id && (
                <span className="text-xs text-muted-foreground">
                  Réf: {ticket.reference_id.slice(0, 8)}
                </span>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mb-2">
              De: {ticket.user_name} ({ticket.user_email})
            </p>
            
            <p className="text-sm line-clamp-2">{ticket.message}</p>
            
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              {formatDate(ticket.created_at)}
              {ticket.replies?.length > 0 && (
                <>
                  <span>•</span>
                  <span>{ticket.replies.length} réponse{ticket.replies.length > 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setShowDetails(!showDetails)}
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              {showDetails ? 'Masquer' : 'Répondre'}
            </Button>
            
            {ticket.status !== 'closed' && (
              <Button 
                size="sm" 
                variant="outline"
                className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                onClick={() => onClose(ticket.id)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Fermer
              </Button>
            )}
          </div>
        </div>

        {/* Reply section */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t">
            <ScrollArea className="h-48 border rounded-lg p-3 mb-3">
              <div className="space-y-3">
                {/* Original message */}
                <div className="p-2 rounded-lg bg-muted">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{ticket.user_name}</span>
                    <Badge variant="outline" className="text-xs">Utilisateur</Badge>
                  </div>
                  <p className="text-sm">{ticket.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{formatDate(ticket.created_at)}</p>
                </div>

                {/* Replies */}
                {ticket.replies?.map((reply, index) => (
                  <div 
                    key={reply.created_at || index}
                    className={`p-2 rounded-lg text-sm ${
                      reply.sender_role === 'admin' 
                        ? 'bg-primary/10 border border-primary/20' 
                        : 'bg-muted'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{reply.sender_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {reply.sender_role === 'admin' ? 'Admin' : 'Utilisateur'}
                      </Badge>
                    </div>
                    <p>{reply.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(reply.created_at)}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {ticket.status !== 'closed' && (
              <div className="flex gap-2">
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Votre réponse..."
                  rows={2}
                  className="flex-1"
                />
                <Button 
                  onClick={handleReply}
                  disabled={sending || !replyMessage.trim()}
                >
                  {sending ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;
